import { AUTH_ERROR_CODES } from './types';

/**
 * 로그인 실패를 화면에 어떻게 보여줄지 정한다.
 *
 * 컴포넌트에서 빼낸 이유 (2026-08-17)
 *   이 판정이 틀리면 **화면상으로는 그럴듯해 보여 눈으로 못 잡는다.**
 *   실제로 첫 화면에 "서버에 연결하지 못했습니다" 가 떠 있었는데,
 *   문구 자체는 우리가 쓴 그대로라 화면만 봐서는 버그로 안 보였다.
 *   로그인이 되니 더더욱 넘어가기 쉬웠다.
 *   activeMenu.ts · batchFormat.ts 를 빼낸 것과 같은 이유다.
 */

/** 로그인 요청의 결과 상태. TanStack Query 의 mutation 에서 이만큼만 뽑아 넘긴다. */
export interface LoginErrorState {
  /** 요청이 실패했는가. 🔴 아직 아무것도 안 보낸 첫 화면에서는 false 다 */
  isError: boolean;
  /** 백엔드가 준 오류 코드. 없으면 서버까지 못 갔다는 뜻이다 */
  code?: string;
  /** 백엔드가 준 메시지 */
  message?: string;
}

/**
 * 화면에 띄울 오류 문구. 띄울 것이 없으면 undefined.
 *
 * 🔴 isError 를 반드시 먼저 본다 — 2026-08-17 에 여기서 버그가 났다
 *
 *   이전 코드는 code 만 보고 "코드가 없으면 서버까지 못 갔다" 로 판정했다.
 *   그 해석은 요청을 보낸 뒤에만 맞다. 아직 아무것도 안 보낸 상태의
 *   undefined 와 구분이 안 돼서, 사이트에 들어가자마자 오류가 떴다.
 *
 *   로그인은 정상으로 됐다. 문구만 거짓말을 하고 있었던 셈이라
 *   "서버가 불안정한가" 로 오해하게 만든다.
 */
export function loginErrorMessage(state: LoginErrorState): string | undefined {
  if (!state.isError) {
    return undefined;
  }

  // 🔴 여기부터는 요청이 실제로 실패한 상태다
  //   코드가 없으면 백엔드에 닿지 못했다는 뜻이다 — 배포 중 재기동, 터널 끊김, 홈서버 다운.
  //   이전에는 undefined 를 돌려줘 화면에 아무것도 안 떴고,
  //   사용자는 버튼만 원래대로 돌아오는 것을 보고 자기가 잘못 눌렀다고 생각하게 됐다.
  if (!state.code) {
    return state.message
      ? `서버에 연결하지 못했습니다. ${state.message}`
      : '서버에 연결하지 못했습니다. 잠시 후 다시 시도해 주세요.';
  }

  // 🔴 A001 은 아이디가 없는 것과 비밀번호가 틀린 것을 구분하지 않는다
  //   구분해 보여주면 어떤 아이디가 실재하는지 알려주는 셈이 된다(계정 열거).
  //   백엔드가 이미 하나로 합쳐 뒀으므로 화면에서도 그대로 쓴다.
  if (state.code === AUTH_ERROR_CODES.ACCOUNT_LOCKED) {
    return `${state.message} 10분 뒤에 자동으로 풀립니다.`;
  }

  return state.message;
}

/**
 * "다시 시도" 버튼을 붙일 상황인가.
 *
 * 서버까지 못 간 경우에만 붙인다. 사용자가 고칠 것이 없고 다시 눌러보면 되는 상황이다.
 *
 * 반대로 비밀번호가 틀린 경우(A001)에는 붙이지 않는다.
 * 같은 값으로 다시 시도하면 똑같이 실패하는데 버튼이 있으면
 * "다시 누르면 될 것 같은" 인상을 준다. 그 자리에서는 입력을 고쳐야 한다.
 */
export function shouldOfferRetry(state: LoginErrorState): boolean {
  return state.isError && !state.code;
}
